import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { AuthShell, EmailField, PasswordField, SubmitButton, validateEmail, validatePassword } from "@/components/AuthFields";

const MODES = [
  { id: "login", label: "Iniciar Sesión" },
  { id: "signup", label: "Crear Cuenta" },
];

const SIGNUP_ERRORS = {
  weak_password: "La contraseña es demasiado débil: usa al menos 6 caracteres.",
  over_email_send_rate_limit: "Se han enviado demasiados emails. Espera unos minutos e inténtalo de nuevo.",
  over_request_rate_limit: "Demasiados intentos seguidos. Espera unos minutos e inténtalo de nuevo.",
  user_already_exists: "Ese email ya tiene una cuenta. Inicia sesión.",
  email_exists: "Ese email ya tiene una cuenta. Inicia sesión.",
  email_address_invalid: "Ese email no es válido. Usa otro.",
  signup_disabled: "El registro de cuentas nuevas está desactivado.",
  unexpected_failure: "Error al crear el perfil del taller. Revisa la función handle_new_user en Supabase.",
};

const errorMessage = (err) =>
  SIGNUP_ERRORS[err.code] ||
  (/database error/i.test(err.message) ? SIGNUP_ERRORS.unexpected_failure : `No se pudo completar: ${err.message}`);

const Tabs = ({ mode, onChange }) => (
  <div className="grid grid-cols-2 gap-1 rounded-lg bg-black/40 p-1">
    {MODES.map((m) => (
      <button key={m.id} type="button" data-testid={`auth-tab-${m.id}`} onClick={() => onChange(m.id)}
        className={`rounded-md py-2 text-sm font-semibold transition-colors duration-200 ${mode === m.id ? "bg-[var(--gold)] text-black" : "text-zinc-400 hover:text-zinc-100"}`}>
        {m.label}
      </button>
    ))}
  </div>
);

const Messages = ({ error, info }) => (
  <>
    {error && <p role="alert" data-testid="login-error" className="text-sm text-red-400">{error}</p>}
    {info && <p data-testid="signup-info" className="text-sm text-emerald-400">{info}</p>}
  </>
);

async function runAuth(mode, email, password) {
  if (mode === "forgot") {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    return error ? { error: errorMessage(error) } : { info: "Si el email existe, te hemos enviado un enlace para restablecer la contraseña." };
  }
  if (mode === "signup") {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error: errorMessage(error) };
    if (data.user && data.user.identities?.length === 0) return { error: SIGNUP_ERRORS.user_already_exists };
    return data.session ? {} : { info: "Cuenta creada. Revisa tu correo y confirma tu email para poder entrar." };
  }
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (!error) return {};
  return { error: error.code === "email_not_confirmed" ? "Tu email aún no está confirmado. Revisa tu correo." : "No se pudo iniciar sesión. Revisa tu email y contraseña." };
}

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState({});
  const [msg, setMsg] = useState({});
  const [busy, setBusy] = useState(false);
  const isSignup = mode === "signup";
  const isForgot = mode === "forgot";

  const emailErr = touched.email ? validateEmail(email) : "";
  const passErr = touched.password && !isForgot ? validatePassword(password, isSignup ? 6 : 0) : "";

  const switchMode = (m) => { setMode(m); setMsg({}); setTouched({}); };
  const onEmail = (v) => { setEmail(v); setTouched((t) => ({ ...t, email: true })); };
  const onPassword = (v) => { setPassword(v); setTouched((t) => ({ ...t, password: true })); };

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (validateEmail(email) || (!isForgot && validatePassword(password, isSignup ? 6 : 0))) return;
    setMsg({});
    setBusy(true);
    setMsg(await runAuth(mode, email.trim(), password));
    setBusy(false);
  };

  return (
    <AuthShell onSubmit={submit}>
      {isForgot ? (
        <div>
          <h1 className="font-display text-xl font-bold">Recuperar contraseña</h1>
          <p className="mt-1 text-sm text-zinc-500">Te enviaremos un enlace para crear una nueva.</p>
        </div>
      ) : <Tabs mode={mode} onChange={switchMode} />}
      <EmailField value={email} onChange={onEmail} error={emailErr} />
      {!isForgot && (
        <PasswordField value={password} onChange={onPassword} error={passErr} hint={isSignup ? "Mínimo 6 caracteres." : ""}
          autoComplete={isSignup ? "new-password" : "current-password"} />
      )}
      {mode === "login" && (
        <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
          className="-mt-2 text-xs text-zinc-400 transition-colors hover:text-[var(--gold)]">¿Olvidaste tu contraseña?</button>
      )}
      <Messages error={msg.error} info={msg.info} />
      <SubmitButton busy={busy}>{isForgot ? "Enviar enlace" : isSignup ? "Crear cuenta" : "Entrar"}</SubmitButton>
      {isForgot && (
        <button type="button" data-testid="back-to-login-link" onClick={() => switchMode("login")}
          className="w-full text-center text-xs text-zinc-400 hover:text-[var(--gold)]">Volver a iniciar sesión</button>
      )}
    </AuthShell>
  );
}
